/**
 * ThemeProvider Component
 *
 * Enables runtime theme customization for multi-tenant white-label support.
 *
 * Features:
 * - Dynamic CSS custom property injection
 * - Tenant-specific theme overrides
 * - React Context for theme consumption
 * - Automatic cleanup on theme changes
 * - TypeScript type safety
 *
 * Usage:
 * ```tsx
 * import { ThemeProvider } from '@quiz-commerce/design-system';
 *
 * <ThemeProvider
 *   tenantId="tenant-123"
 *   theme={{
 *     primaryColor: '#FF5722',
 *     backgroundColor: '#FFFFFF',
 *   }}
 * >
 *   <App />
 * </ThemeProvider>
 * ```
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';

/**
 * Tenant theme customization options
 *
 * All properties are optional - only specify overrides needed.
 * Unspecified properties will use default semantic token values.
 */
export interface TenantTheme {
  /** Primary brand color - overrides interactive primary buttons, links, etc. */
  primaryColor?: string;

  /** Secondary interactive color - overrides secondary buttons */
  secondaryColor?: string;

  /** Success/positive action color - overrides success buttons, confirmations */
  successColor?: string;

  /** Danger/destructive action color - overrides delete buttons, errors */
  dangerColor?: string;

  /** Accent color - overrides special highlights, badges */
  accentColor?: string;

  /** Primary background color */
  backgroundColor?: string;

  /** Secondary/alternate background color */
  backgroundSecondary?: string;

  /** Primary text color */
  textColor?: string;

  /** Secondary/muted text color */
  textSecondary?: string;

  /** Primary border color */
  borderColor?: string;

  /** Border radius for buttons (e.g., "0.5rem", "8px") */
  borderRadius?: string;

  /** Border radius for cards/panels */
  borderRadiusCard?: string;

  /** Custom font family */
  fontFamily?: string;
}

/**
 * Theme context value shape
 */
interface ThemeContextValue {
  /** Current tenant identifier */
  tenantId: string | null;

  /** Current theme configuration */
  theme: TenantTheme | null;

  /** Update theme dynamically */
  setTheme: (theme: TenantTheme) => void;
}

/**
 * Theme Context
 *
 * Provides theme state to consuming components.
 * Use `useTenantTheme()` hook to access context.
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * ThemeProvider Props
 */
export interface ThemeProviderProps {
  /** Child components to receive theme context */
  children: ReactNode;

  /** Unique tenant identifier for multi-tenant tracking */
  tenantId?: string;

  /** Theme customization overrides */
  theme?: TenantTheme;
}

/**
 * Mapping of theme properties to CSS custom property names
 *
 * This mapping defines which semantic tokens get overridden
 * by each theme property.
 */
const THEME_TOKEN_MAPPING: Record<keyof TenantTheme, string[]> = {
  primaryColor: [
    '--semantic-color-interactive-primary-default',
    '--semantic-color-text-link',
  ],
  secondaryColor: [
    '--semantic-color-interactive-secondary-default',
  ],
  successColor: [
    '--semantic-color-interactive-success-default',
  ],
  dangerColor: [
    '--semantic-color-interactive-danger-default',
  ],
  accentColor: [
    '--semantic-color-interactive-accent-default',
  ],
  backgroundColor: [
    '--semantic-color-background-primary',
    '--semantic-color-surface-default',
  ],
  backgroundSecondary: [
    '--semantic-color-background-secondary',
  ],
  textColor: [
    '--semantic-color-text-primary',
  ],
  textSecondary: [
    '--semantic-color-text-secondary',
  ],
  borderColor: [
    '--semantic-color-border-default',
  ],
  borderRadius: [
    '--semantic-borderRadius-button',
    '--reference-borderRadius-lg',
  ],
  borderRadiusCard: [
    '--semantic-borderRadius-card',
  ],
  fontFamily: [
    // Font family would need to be applied differently (not a token)
  ],
};

/**
 * Apply theme overrides to DOM via CSS custom properties
 *
 * @param theme - Theme configuration object
 * @param root - HTML element to apply styles to (typically document.documentElement)
 */
function applyTheme(theme: TenantTheme, root: HTMLElement): void {
  // Iterate over theme properties
  Object.entries(theme).forEach(([key, value]) => {
    if (!value) return;

    const tokenNames = THEME_TOKEN_MAPPING[key as keyof TenantTheme];
    if (!tokenNames) return;

    // Apply value to all mapped CSS custom properties
    tokenNames.forEach((tokenName) => {
      root.style.setProperty(tokenName, value);
    });
  });

  // Handle font family separately (applied to body)
  if (theme.fontFamily) {
    document.body.style.fontFamily = theme.fontFamily;
  }
}

/**
 * Remove theme overrides from DOM
 *
 * @param theme - Theme configuration to remove
 * @param root - HTML element to remove styles from
 */
function removeTheme(theme: TenantTheme, root: HTMLElement): void {
  Object.entries(theme).forEach(([key, value]) => {
    if (!value) return;

    const tokenNames = THEME_TOKEN_MAPPING[key as keyof TenantTheme];
    if (!tokenNames) return;

    // Remove custom property overrides
    tokenNames.forEach((tokenName) => {
      root.style.removeProperty(tokenName);
    });
  });

  // Reset font family
  if (theme.fontFamily) {
    document.body.style.fontFamily = '';
  }
}

/**
 * ThemeProvider Component
 *
 * Wraps application or component tree to provide theme context
 * and apply theme customizations via CSS custom properties.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  tenantId = null,
  theme: initialTheme = null,
}) => {
  const [currentTheme, setCurrentTheme] = React.useState<TenantTheme | null>(initialTheme);

  // Apply theme when tenantId or theme changes
  useEffect(() => {
    const root = document.documentElement;

    // Set tenant ID as data attribute for debugging/styling
    if (tenantId) {
      root.setAttribute('data-tenant-id', tenantId);
    } else {
      root.removeAttribute('data-tenant-id');
    }

    // Apply theme if provided
    if (currentTheme) {
      applyTheme(currentTheme, root);
    }

    // Cleanup function: remove theme on unmount or change
    return () => {
      root.removeAttribute('data-tenant-id');
      if (currentTheme) {
        removeTheme(currentTheme, root);
      }
    };
  }, [tenantId, currentTheme]);

  const contextValue: ThemeContextValue = {
    tenantId: tenantId || null,
    theme: currentTheme,
    setTheme: setCurrentTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to consume theme context
 *
 * @returns Theme context value with tenantId, theme, and setTheme
 * @throws Error if used outside ThemeProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { tenantId, theme, setTheme } = useTenantTheme();
 *
 *   return (
 *     <div>
 *       <p>Current tenant: {tenantId}</p>
 *       <button onClick={() => setTheme({ primaryColor: '#FF0000' })}>
 *         Change Theme
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTenantTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTenantTheme must be used within a ThemeProvider');
  }

  return context;
}

/**
 * Higher-order component to inject theme as props
 *
 * @param Component - Component to wrap
 * @returns Wrapped component with theme props
 *
 * @example
 * ```tsx
 * interface MyComponentProps {
 *   theme: ThemeContextValue;
 * }
 *
 * const MyComponent: React.FC<MyComponentProps> = ({ theme }) => (
 *   <div>Tenant: {theme.tenantId}</div>
 * );
 *
 * export default withTheme(MyComponent);
 * ```
 */
export function withTheme<P extends { theme: ThemeContextValue }>(
  Component: React.ComponentType<P>
) {
  return function ThemedComponent(props: Omit<P, 'theme'>) {
    const theme = useTenantTheme();
    return <Component {...(props as P)} theme={theme} />;
  };
}
